import { RemoveYear, getLatestChangeDate, getLatestChangeReason, hasInErrorReason } from "./helpers.js";
class LeaveSummarySearchImpl {
    constructor() {
        this.format = (response) => {
            const searchResultMetadata = {
                startIndex: response.searchResultMetaData?.startIndex,
                totalResults: response.searchResultMetaData?.totalResults,
                itemsPerPage: response.searchResultMetaData?.itemsPerPage,
                timeTaken: response.searchResultMetaData?.resultMetadata?.timeTaken,
                links: response.searchResultMetaData?.resultMetaData?._links
            };
            const LeaveSearch = (response.leaves ?? [])
                .filter((leave) => {
                if (!leave.employee || !leave.leavePeriod)
                    return false;
                const hasLeaveError = hasInErrorReason(leave.leaveCloseReason, leave.leaveCloseReasonDesc);
                const validPeriods = (leave.leavePeriod || []).filter((period) => !hasInErrorReason(period.periodStatusReason, period.leavePeriodStatusReasonDesc));
                return !hasLeaveError && validPeriods.length > 0;
            })
                .map((leave) => {
                const validPeriods = (leave.leavePeriod || []).filter((period) => !hasInErrorReason(period.periodStatusReason, period.leavePeriodStatusReasonDesc));
                const validRelatedClaims = (leave.relatedClaimDetails || []).filter((claim) => !hasInErrorReason(claim?.claimProcessingStage, claim?.customClaimStatusReason, claim?.claimStatusReason, claim?.claimStatusReasonDesc, claim?.claimStatusReasonCdDesc, claim?.customClaimStatusReasonCdDesc));
                const normalizedLeave = {
                    ...leave,
                    leavePeriod: validPeriods,
                    relatedClaimDetails: validRelatedClaims,
                };
                return this.mapLeaveToSummary(normalizedLeave, leave);
            });
            return { searchResultMetadata, LeaveSearch };
        };
    }
    extractLeavePeriodData(leave) {
        const reportGroupNumber = leave.leavePeriod?.length
            ? leave.leavePeriod[0].reportGroupNumber
            : "—";
        const reportGroupName = leave.leavePeriod?.length
            ? leave.leavePeriod[0].reportGroupName
            : "—";
        const employerReportGroupNumber = leave.leavePeriod?.length
            ? leave.leavePeriod[0].employerReportGroupNumber
            : "—";
        const leaveScheduleTypes = [
            ...new Set((leave.leavePeriod || []).map((period) => period.leaveScheduleType)),
        ].join(", ");
        let actualDeliveryDt = "—";
        if (leave.leavePeriod?.length) {
            actualDeliveryDt = leave.leavePeriod[0].actualDeliveryDt
                ? RemoveYear(leave.leavePeriod[0].actualDeliveryDt)
                : null;
        }
        const protectionNames = [
            ...new Set((leave.leavePeriod || []).flatMap((period) => period.leavePeriodProtection?.map((p) => p.protectionName) || [])),
        ].join(", ");
        const classificationTypeDesc = [
            ...new Set((leave.leavePeriod || []).flatMap((period) => period.leavePeriodClassification?.map((c) => c.classificiationTypeDesc) || [])),
        ].join(", ");
        return {
            reportGroupNumber,
            reportGroupName,
            employerReportGroupNumber,
            leaveScheduleTypes,
            actualDeliveryDt,
            protectionNames,
            classificationTypeDesc,
        };
    }
    getHomeDigitalAddress(employee) {
        const homeDigitalAddress = employee.digitalAddresses?.find((address) => address.digitalAddressTypeCd === "HE");
        const homeDigitalAddressFineos = employee.digitalAddresses?.find((address) => address.digitalAddressType?.toLowerCase() === "personal email");
        return homeDigitalAddress?.digitalAddressText ?? homeDigitalAddressFineos?.digitalAddressText ?? null;
    }
    getWorkDigitalAddress(employee) {
        const workDigitalAddress = employee.digitalAddresses?.find((address) => address.digitalAddressTypeCd === "BE");
        const workDigitalAddressFineos = employee.digitalAddresses?.find((address) => address.digitalAddressType?.toLowerCase() === "work email");
        if (workDigitalAddress) {
            return workDigitalAddress.digitalAddressText ?? null;
        }
        if (workDigitalAddressFineos) {
            return workDigitalAddressFineos.digitalAddressText ?? null;
        }
        return null;
    }
    processRelatedClaims(leave) {
        const relatedClaims = leave.relatedClaimDetails?.length
            ? leave.relatedClaimDetails
            : [{ claimId: "", claimCoverageTypeCd: "", claimStatus: "", disabilityBeginDt: "", policyAdminSystemCd: "", groupAgreementId: "", policyName: "", divisionId: "", divisionName: "", claimClosedDt: "" }];
        const closedDt = relatedClaims[0].claimClosedDt;
        const filterAndJoin = (claims, property, transform = (v) => v) => {
            return claims
                .map((claim) => transform(claim[property]))
                .filter((value) => value !== null && value !== undefined && value !== "")
                .join(", ");
        };
        const relatedStdClaimIds = filterAndJoin(relatedClaims, "claimId");
        const relatedStdClaimStatuses = filterAndJoin(relatedClaims, "claimStatus");
        const relatedDisabilityBeginDt = filterAndJoin(relatedClaims, "disabilityBeginDt", RemoveYear);
        return { closedDt, relatedStdClaimIds, relatedStdClaimStatuses, relatedDisabilityBeginDt };
    }
    calculateRTWDates(leave) {
        let partTimeRTWDt = "—";
        if (leave.rtwType === "Part Time") {
            partTimeRTWDt = leave.rtwDt ? RemoveYear(leave.rtwDt) : null;
        }
        let fullTimeRTWDt = "—";
        if (leave.rtwType === "Full Time") {
            fullTimeRTWDt = leave.rtwDt ? RemoveYear(leave.rtwDt) : null;
        }
        return { partTimeRTWDt, fullTimeRTWDt };
    }
    mapLeaveToSummary(leave, originalLeave) {
        const postalAddresses = leave.employee?.postalAddresses || [{ addressType: "" }];
        const addressTypeToFilter = leave.leaveSourceSystem === "FNEOS" ? "Home" : "PA";
        const selectedAddress = postalAddresses
            .filter((address) => address.addressType === addressTypeToFilter)
            .reduce((latest, current) => new Date(String(current.beginDate)) > new Date(String(latest.beginDate)) ? current : latest, { beginDate: "01/01/1900" });
        const leavePeriodData = this.extractLeavePeriodData(leave);
        const workDigitalAddressText = this.getWorkDigitalAddress(leave.employee);
        const digitalAddressText = this.getHomeDigitalAddress(leave.employee);
        const { closedDt, relatedStdClaimIds, relatedStdClaimStatuses, relatedDisabilityBeginDt } = this.processRelatedClaims(leave);
        const relatedClaimLinkedInd = leave.relatedClaimDetails?.length
            ? originalLeave.relatedClaimLinkedInd
            : "N";
        const { partTimeRTWDt, fullTimeRTWDt } = this.calculateRTWDates(leave);
        return {
            lastName: leave.employee?.lastName,
            jobTitle: leave.employee?.jobTitle,
            firstName: leave.employee?.firstName,
            taxIdentificationNumber: leave.employee?.taxIdentificationNumber,
            employeeId: leave.employee?.employeeId,
            partTimeRTWDt,
            fullTimeRTWDt,
            personDisplayName: leave.leaveSpecialist?.personDisplayName,
            coverageType: leave.coverageType,
            statusEffectiveDt: leave.leaveStatusEffectiveDt,
            lastDateWorked: leave.lastDateWorked,
            leaveId: leave.leaveId,
            historicalLeaveInd: leave.historicalLeaveInd,
            leaveReason: leave.leaveReason,
            leaveRequestDt: leave.leaveRequestDt,
            leaveScheduleType: leavePeriodData.leaveScheduleTypes,
            reportGroupNumber: leavePeriodData.reportGroupNumber,
            reportGroupName: leavePeriodData.reportGroupName,
            employerReportGroupNumber: leavePeriodData.employerReportGroupNumber,
            leaveStatus: leave.leaveStatus,
            managerName: leave.employee?.managerName,
            managerEmail: leave.employee?.managerEmail,
            fullName: leave.employee?.name,
            receivedDt: leave.leaveReceivedDt,
            relatedClaimLinkedInd,
            rtwStatus: leave.rtwStatus,
            workState: leave.employee?.employeeWorkStateCd,
            protectionName: leavePeriodData.protectionNames,
            leaveStartDt: leave.leaveStartDt,
            leaveEndDt: leave.leaveEndDt,
            dateOfBirth: leave.employee?.dateOfBirth,
            age: leave.ageAtLeaveStart,
            classificiationTypeDesc: leavePeriodData.classificationTypeDesc,
            digitalAddressText: digitalAddressText,
            workDigitalAddressText,
            fitForDutyReleaseDt: leave.fitForDutyReleaseDt,
            claimClosedDt: closedDt,
            city: selectedAddress.city,
            genderAtBirth: leave.employee?.genderAtBirth,
            hireDt: leave.employee?.hireDt,
            earningType: leave.employee?.earningType,
            employerLocationName: leave.employee?.employerLocationName,
            firstHireDt: leave.employee?.firstHireDt,
            jurisdictionCode: selectedAddress?.jurisdictionCode ?? null,
            addressLineOne: selectedAddress?.addressLineOne ?? null,
            addressLineTwo: selectedAddress?.addressLineTwo ?? null,
            rtwLastUpdateDt: leave.rtwLastUpdateDt,
            tenureDays: leave.employee?.tenureDays,
            postalCode: selectedAddress.postalCode,
            leaveCloseReason: leave.leaveCloseReason,
            relatedStdClaimId: relatedStdClaimIds,
            relatedStdClaimStatus: relatedStdClaimStatuses,
            relatedDisabilityBeginDt,
            pregnancyRelatedInd: leave.pregnancyRelatedInd,
            actualDeliveryDt: leavePeriodData.actualDeliveryDt ? RemoveYear(leavePeriodData.actualDeliveryDt) : null,
            leaveSubmitMethod: leave.leaveSubmissionSource,
            changeReason: getLatestChangeReason(leave),
            chgDate: getLatestChangeDate(leave),
            weeklyCoveredSalaryAmt: leave.weeklyCoveredSalaryAmt,
        };
    }
}
export const LeaveSummarySearch = new LeaveSummarySearchImpl();
